import React, { useState, FormEvent, ReactNode, useEffect, useCallback, useRef } from 'react';
import styles from './Login.module.scss';
import { useCaptchaGeneration } from 'lib/hooks/useCaptchaGeneration';
import { signIn, getSession } from 'next-auth/react';
import { BuiltInProviderType } from 'next-auth/providers';
import { useQueryStringParameters } from 'lib/hooks/browser/useQueryString';
import { SvgIcon } from 'components/helpers/SvgIcon';
import CtaButton from 'components/helpers/Atoms/Cta/CtaButton';
import { MessageBanner } from 'components/helpers/Atoms/FormElements/MessageBanner';
import { FormField } from 'components/helpers/Atoms/FormElements/FormField';
import { TextInput } from 'components/helpers/Atoms/FormElements/TextInput';
import { PasswordInputField } from 'components/helpers/Atoms/FormElements/PasswordInputField';
import { Checkbox } from 'components/helpers/Atoms/FormElements/Checkbox';
import clsx from 'clsx';
import router from 'next/router';
import useDictionary from 'lib/hooks/useDictionary';
import { RecaptchaBadge } from 'components/helpers/Recaptcha';
import { useFormErrors } from 'lib/hooks/useFormErrors';
import ExistingUser, {
  getSocialOAuthDetails,
} from 'components/authorable/shared/userprofile/SignUp/ExistingUser';
import { removeCookie } from 'lib/utils/cookies';
import { useSessionSafe } from 'lib/hooks/auth/useSessionSafe';
import { DataLayerEvent, pushToDataLayer } from 'components/helpers/Global/Analytics/DataLayer';

const errorMessages: Record<string, string> = {
  GENERIC_ERROR: 'Components.Login.GenericError',
  AUTHENTICATION_FAILED: 'Components.Login.AuthenticationFailed',
  INVALID_CAPTCHA: 'Components.Login.InvalidCaptcha',
  MISSING_CREDENTIALS: 'Components.Login.MissingCredentials',
  USER_PROFILE_ERROR: 'Components.Login.UserProfileError',
  RESET_PASSWORD_REQUIRED: 'Components.Login.ResetPasswordRequired',
  USER_NOT_FOUND: 'Components.Login.AuthenticationFailed',
  INVALID_EMAIL: 'Components.Login.InvalidEmail',
};

// Form field IDs - maintained in one place for consistency
const FORM_FIELD_IDS = {
  USERNAME: 'username',
  PASSWORD: 'password',
  REMEMBER_ME: 'rememberMe',
} as const;

// Error message element IDs for aria-describedby linkage (screen reader support)
const FORM_ERROR_IDS = {
  USERNAME: `${FORM_FIELD_IDS.USERNAME}-error`,
};

export interface LoginProps {
  skipCaptcha: boolean;
  enableRememberMe: boolean;
  enableGoogleLogin: boolean;
  enableFacebookLogin: boolean;
  description: ReactNode;
  emailLabel: ReactNode;
  emailPlaceholderText: string;
  forgotPasswordLink: ReactNode;
  headline: ReactNode;
  loginButtonText: ReactNode;
  passwordLabel: ReactNode;
  passwordPlaceholderText: string;
  remembermeText: ReactNode;
  signupLink: ReactNode;

  alreadyMemberHeading?: ReactNode;
  alreadyMemberDescription?: ReactNode;
  verifyEmailLabel?: ReactNode;
  verifyEmailPlaceholder?: string;
  socialConnectSubmitText?: ReactNode;
  socialConnectCancelLink?: ReactNode;
}

export default function Login(props: LoginProps) {
  const {
    skipCaptcha,
    enableRememberMe,
    enableGoogleLogin,
    enableFacebookLogin,
    description,
    emailLabel,
    emailPlaceholderText,
    forgotPasswordLink,
    headline,
    loginButtonText,
    passwordLabel,
    passwordPlaceholderText,
    remembermeText,
    signupLink,
    alreadyMemberHeading,
    alreadyMemberDescription,
    verifyEmailLabel,
    verifyEmailPlaceholder,
    socialConnectSubmitText,
    socialConnectCancelLink,
  } = props;

  const { data: session } = useSessionSafe();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [disableSubmit, setDisableSubmit] = useState(false);
  const [returnUrl, callbackUrl, queryParamError] = useQueryStringParameters(
    'returnUrl',
    'callbackUrl',
    'error'
  );
  const [loginError, setLoginError] = useState(queryParamError || '');
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const [showSocialConnect, setShowSocialConnect] = useState(false);
  const [pendingFocusId, setPendingFocusId] = useState<string | null>(null);

  const queryParamRedirectUrl = returnUrl || callbackUrl;

  const { captchaToken } = useCaptchaGeneration('login_captcha', !skipCaptcha);

  const captchaTokenRef = useRef<string | null>(null);

  const removeRecaptchaTokenFromDom = useCallback(() => {
    if (typeof document === 'undefined') return;

    const tokenInputs = Array.from(
      document.querySelectorAll(
        '#recaptcha-token, input[type="hidden"][id="recaptcha-token"], input[type="hidden"][name="recaptcha-token"]'
      )
    );

    for (const input of tokenInputs) {
      input.parentNode?.removeChild(input);
    }
  }, []);

  const hideAllSocialLogins = !enableGoogleLogin && !enableFacebookLogin;

  const { getDictionaryValue } = useDictionary();
  const { setFieldError, clearAllErrors, getFieldError, hasErrors } = useFormErrors();

  // Check for social connect cookie on mount
  useEffect(() => {
    // Clear fromSignupPage cookie since user is now on login page
    // This prevents incorrect redirects if user previously started signup OAuth flow
    removeCookie('fromSignupPage');

    const oauthDetails = getSocialOAuthDetails();
    // Show social connect form for existing users who need to link their account
    if (oauthDetails && !oauthDetails.isNewUser) {
      setShowSocialConnect(true);
    }
  }, []);

  useEffect(() => {
    if (queryParamError) {
      const errorKey = errorMessages[queryParamError];
      setLoginError(
        (errorKey ? getDictionaryValue(errorKey) : '') ||
          getDictionaryValue(errorMessages['GENERIC_ERROR']) ||
          queryParamError
      );
      removeRecaptchaTokenFromDom();
    }
  }, [queryParamError, getDictionaryValue, removeRecaptchaTokenFromDom]);

  useEffect(() => {
    if (session && session.user) {
      router.push(queryParamRedirectUrl || '/');
    }
  }, [session, queryParamRedirectUrl]);

  // WCAG 2.4.3: after a failed submit, move focus to the first invalid field so the
  // screen reader announces its field-level error. The 100 ms delay lets the
  // role="alert" banner announce first.
  useEffect(() => {
    if (!pendingFocusId) return;
    const timer = setTimeout(() => {
      document.getElementById(pendingFocusId)?.focus();
      setPendingFocusId(null);
    }, 100);
    return () => clearTimeout(timer);
  }, [pendingFocusId]);

  const handleUsernameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const rawValue = e.target.value;
      const trimmedValue = rawValue.trim();
      setUsername(rawValue);

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (trimmedValue && !emailRegex.test(trimmedValue)) {
        setFieldError(
          FORM_FIELD_IDS.USERNAME,
          getDictionaryValue(errorMessages.INVALID_EMAIL, 'Please enter a valid email address.')
        );
      } else {
        setFieldError(FORM_FIELD_IDS.USERNAME, '');
      }
    },
    [getDictionaryValue, setFieldError]
  );

  const handleUsernamePaste = useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>) => {
      const input = e.currentTarget;
      setTimeout(() => {
        handleUsernameChange({
          target: { value: input.value },
        } as React.ChangeEvent<HTMLInputElement>);
      }, 0);
    },
    [handleUsernameChange]
  );

  if (session?.user) {
    return <></>;
  }

  // If social connect flow is active, show the ExistingUser component
  if (showSocialConnect) {
    return (
      <div className={styles.loginContainer}>
        <ExistingUser
          alreadyMemberHeading={alreadyMemberHeading}
          alreadyMemberDescription={alreadyMemberDescription}
          verifyEmailLabel={verifyEmailLabel}
          verifyEmailPlaceholder={verifyEmailPlaceholder}
          socialConnectSubmitText={socialConnectSubmitText}
          cancelLink={socialConnectCancelLink}
          forgotPasswordLink={forgotPasswordLink}
          passwordLabel={passwordLabel}
          passwordPlaceholderText={passwordPlaceholderText}
        />
      </div>
    );
  }

  const handleSubmit = async (e: FormEvent, provider: BuiltInProviderType) => {
    e.preventDefault();
    setLoginError('');
    setHasAttemptedSubmit(true);
    clearAllErrors();

    // Validate required fields for credentials provider
    if (provider === 'credentials') {
      let emailValidationError = '';
      let passwordValidationError = '';

      // Validate username/email
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const trimmedUsername = username.trim();
      if (!trimmedUsername) {
        emailValidationError = getDictionaryValue(
          'Components.Login.EmailRequired',
          'Email is required.'
        );
        setFieldError(FORM_FIELD_IDS.USERNAME, emailValidationError);
      } else if (!emailRegex.test(trimmedUsername)) {
        emailValidationError = getDictionaryValue(
          errorMessages.INVALID_EMAIL,
          'Please enter a valid email address.'
        );
        setFieldError(FORM_FIELD_IDS.USERNAME, emailValidationError);
      }

      // Validate password
      if (!password.trim()) {
        passwordValidationError = getDictionaryValue(
          'Components.Login.PasswordRequired',
          'Password is required.'
        );
        setFieldError(FORM_FIELD_IDS.PASSWORD, passwordValidationError);
      }

      // Update banner message if there are validation errors
      if (emailValidationError || passwordValidationError) {
        const missingFieldsMessage = getDictionaryValue(
          'Components.Login.EmptyFieldsSubmission',
          'There was an error logging you in.| Please check the below errors and try again.'
        );

        // Append the specific field errors to the banner so the role="alert"
        // announces every error (e.g. both "Email is required." and
        // "Password is required."), not just the focused field.
        const summarizedErrors = Array.from(
          new Set([emailValidationError, passwordValidationError].filter(Boolean))
        ).join(' ');
        setLoginError(
          missingFieldsMessage
            ? `${missingFieldsMessage} ${summarizedErrors}`.trim()
            : summarizedErrors
        );
        setDisableSubmit(false);

        // Move focus to the first invalid field so screen readers announce its error.
        setPendingFocusId(emailValidationError ? FORM_FIELD_IDS.USERNAME : FORM_FIELD_IDS.PASSWORD);
        return;
      }
    }

    setDisableSubmit(true);
    try {
      // token should only exist when captcha is enabled
      let token: string | undefined;

      if (!skipCaptcha) {
        // Generate a NEW captcha token for THIS request (tokens are single-use).
        // Do this right before calling signIn() to avoid token expiry.
        const generated = await captchaToken().catch(() => null);

        // cache last good token
        if (generated) {
          captchaTokenRef.current = generated;
        }

        // Cache the token across attempts; if generation returns null but we still
        // have a previous token use the previous token.
        token = generated ?? captchaTokenRef.current ?? undefined;

        // Require token ONLY when captcha is enabled
        if (!token) {
          setLoginError(
            getDictionaryValue(errorMessages.INVALID_CAPTCHA, 'Captcha verification failed')
          );
          return;
        }
      }
      // For social login, construct the callback URL with returnUrl query param
      // NextAuth will parse this URL and extract returnUrl for the social connect flow
      // We build this from our known state values rather than window.location
      // to ensure consistency with the parsed query params
      const buildSocialCallbackUrl = () => {
        const basePath = typeof window !== 'undefined' ? window.location.pathname : '/';
        if (queryParamRedirectUrl) {
          return `${basePath}?returnUrl=${encodeURIComponent(queryParamRedirectUrl)}`;
        }
        return basePath;
      };

      const requestParams =
        provider === 'credentials'
          ? {
              redirect: false,
              password: password,
              username: username.trim(),
              ...(skipCaptcha ? {} : { captchaToken: token }),
              rememberMe: rememberMe,
            }
          : {
              redirect: false,
              callbackUrl: buildSocialCallbackUrl(),
              ...(skipCaptcha ? {} : { captchaToken: token }),
              rememberMe: false,
            };

      const signInResponse = await signIn(provider, requestParams);

      if (signInResponse?.error) {
        if (provider === 'credentials') {
          removeRecaptchaTokenFromDom();
        }
        // Clear field errors when API error occurs (these are form-level errors)
        clearAllErrors();
        const errorKey = errorMessages[signInResponse.error];
        setLoginError(
          (errorKey ? getDictionaryValue(errorKey) : '') ||
            getDictionaryValue(errorMessages.GENERIC_ERROR)
        );
        removeRecaptchaTokenFromDom();
      } else {
        const sessionData = await getSession();
        const gtmEventPayload: DataLayerEvent = {
          event: 'login',
          user_id: sessionData?.user?.id ?? null,
          user_status: 'Authenticated',
        };
        pushToDataLayer(gtmEventPayload);
        setLoginError('');
        clearAllErrors();
        if (provider === 'credentials') {
          router.push(queryParamRedirectUrl || '/');
        }
      }
    } catch (error) {
      console.error('Login error:', error);
      setLoginError(
        getDictionaryValue(errorMessages.GENERIC_ERROR) ||
          'An unexpected error occurred. Please try again later.'
      );
    } finally {
      setDisableSubmit(false);
    }
  };

  return (
    <div className={styles.loginContainer}>
      <div className={styles.login}>
        <div className={styles.header}>
          <div className={styles.headerContent}>
            <h1 className={styles.title}>{headline}</h1>
            {description && signupLink && (
              <div className={styles.descriptionAndSignupLink}>
                {description}
                {signupLink}
              </div>
            )}
          </div>
          <hr className={styles.divider} />
        </div>

        <div className={styles.content}>
          {loginError && <MessageBanner type="error" message={loginError} />}

          <span className={styles.requiredIndicator}>
            {getDictionaryValue('Components.MyAccount.Required', '* Required')}
          </span>

          <form className={styles.form} onSubmit={(e) => handleSubmit(e, 'credentials')} noValidate>
            <div className={styles.inputs}>
              <div className={clsx(styles.formFields)}>
                <FormField
                  id="email-label"
                  label={emailLabel}
                  labelFor={FORM_FIELD_IDS.USERNAME}
                  errorId={FORM_ERROR_IDS.USERNAME}
                  error={!!getFieldError(FORM_FIELD_IDS.USERNAME)}
                  required
                >
                  <TextInput
                    id={FORM_FIELD_IDS.USERNAME}
                    type="email"
                    aria-labelledby="email-label"
                    errorId={FORM_ERROR_IDS.USERNAME}
                    value={username}
                    onChange={handleUsernameChange}
                    onPaste={handleUsernamePaste}
                    placeholder={emailPlaceholderText}
                    error={!!getFieldError(FORM_FIELD_IDS.USERNAME) || (!hasErrors && !!loginError)}
                    errorMessage={
                      getFieldError(FORM_FIELD_IDS.USERNAME)
                        ? (getFieldError(FORM_FIELD_IDS.USERNAME) as string)
                        : undefined
                    }
                    required
                  />
                </FormField>
                <PasswordInputField
                  id={FORM_FIELD_IDS.PASSWORD}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  label={passwordLabel}
                  placeholder={passwordPlaceholderText}
                  showInlineError={hasAttemptedSubmit && !!getFieldError(FORM_FIELD_IDS.PASSWORD)}
                  error={
                    hasAttemptedSubmit && getFieldError(FORM_FIELD_IDS.PASSWORD)
                      ? (getFieldError(FORM_FIELD_IDS.PASSWORD) as string)
                      : !hasErrors
                        ? loginError
                        : undefined
                  }
                  required
                />
              </div>

              {(forgotPasswordLink || enableRememberMe) && (
                <div className={styles.checkboxAndForgotPassword}>
                  {enableRememberMe && (
                    <Checkbox
                      id={FORM_FIELD_IDS.REMEMBER_ME}
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      label={remembermeText}
                    />
                  )}
                  {forgotPasswordLink}
                </div>
              )}
            </div>

            <CtaButton
              ctaStyle="filled"
              ctaVariant="brand"
              type="submit"
              className={styles.loginButton}
              disabled={disableSubmit}
            >
              {loginButtonText}
            </CtaButton>
          </form>

          {!hideAllSocialLogins && (
            <div className={styles.orOptions}>
              <hr className={styles.divider} />

              <span className={styles.orText}>
                {getDictionaryValue('Common.LoginAndRegistration.OR', 'OR')}
              </span>
              <hr className={styles.divider} />
            </div>
          )}

          {!hideAllSocialLogins && (
            <div className={styles.socialLogins}>
              {enableGoogleLogin && (
                <CtaButton
                  ctaStyle="outline"
                  ctaVariant="brand"
                  className={styles.socialButton}
                  onClick={(e) => handleSubmit(e, 'google')}
                >
                  <SvgIcon fill="none" className={styles.socialIcon} icon="google" size="sm" />
                  <span>
                    {getDictionaryValue(
                      'Common.LoginAndRegistration.ContinueWithGoogle',
                      'Continue with Google'
                    )}
                  </span>
                </CtaButton>
              )}
              {enableFacebookLogin && (
                <CtaButton
                  ctaStyle="outline"
                  ctaVariant="brand"
                  className={styles.socialButton}
                  onClick={(e) => handleSubmit(e, 'facebook')}
                >
                  <SvgIcon fill="none" className={styles.socialIcon} icon="facebook" size="sm" />
                  <span>
                    {getDictionaryValue(
                      'Common.LoginAndRegistration.ContinueWithFacebook',
                      'Continue with Facebook'
                    )}
                  </span>
                </CtaButton>
              )}
            </div>
          )}
          {/* show RecaptchaBadge only in case of skipCaptcha false */}
          {!skipCaptcha && <RecaptchaBadge />}
        </div>
      </div>
    </div>
  );
}
